// AdminClients.jsx
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";
import "../AdminDashboard.css";
import { useNavigate } from "react-router-dom";
import NoIndex from "../../NoIndex";
import { UserCheck, UserX, Eye, ExternalLink } from "lucide-react";
import MyContext from "../../../MyContext"; // ContextAPI
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import ClientConfigModal from "./ClientConfigModal";

const ClientAccordionTable = ({ Admin }) => {
  // State ########################################################################################################
  const navigate = useNavigate();
  const { baseUrl, config } = useContext(MyContext);

  const [Clients, setClients] = useState([]);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [userID, setuserID] = useState("");
  const [Credentials, setCredentials] = useState();
  const [appId, setAppId] = useState("");
  const [businessAccId, setBusinessAccId] = useState("");
  const [token, setToken] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [Loading, setLoading] = useState(false);

  // Track any busy action for a user (prevents re-clicks)
  // Values can be: "subscribe" | "register" | "activate" | "deactivate" | "upgrade" | "whatsapp" | etc.
  const [busyAction, setBusyAction] = useState({});

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  // Upgrade subscription
  const [extendDays, setExtendDays] = useState("30");
  const [extendLoading, setExtendLoading] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // NEW: details modal
  const [showDetails, setShowDetails] = useState(false);
  // NEW: gateway update
  const [paymentGateway, setPaymentGateway] = useState("");
  const [showGatewayForm, setShowGatewayForm] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);



  // API Calls #####################################################################################################
  const FetchClientCall = async () => {
    setLoading(true);
    try {
      const UserDetail = {
        userid: Admin.pkid,
        latitude,
        longitude,
      };

      const response = await axios.post(
        `${baseUrl}/api/Admin/getAllClientList`,
        UserDetail,
        config
      );
      if (response.data.responseCode === "00") {
        setClients(response.data.response);
      } else {
        toast.error(response.data.responseMessage || "Failed to fetch clients");
      }
    } catch (error) {
      toast.error("Error fetching clients");
      console.error("getAllClientList error:", error);
    } finally {
      setLoading(false);
    }
  };

  const FetchClientCredentialsCall = async (userId) => {
    try {
      const UserDetail = {
        userid: userId,
        secret: "string",
        fromdate: "string",
        todate: "string",
        status: ["string"],
      };

      const response = await axios.post(
        `${baseUrl}/api/Whatsapp/getWhatsappCredentials`,
        UserDetail,
        config
      );
      if (response.data.responseCode === "00") {
        setCredentials(response.data.response);
      } else {
        toast.error(
          response.data.responseMessage || "Failed to fetch credentials"
        );
      }
    } catch (error) {
      toast.error("Error while fetching credentials");
      console.error("getWhatsappCredentials error:", error);
    }
  };

  const DeactivateClientAPI = async (usCode) => {
    const result = await Swal.fire({
      title: "Deactivate client?",
      text: "You won't be able to revert this.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, deactivate",
    });
    if (!result.isConfirmed) return;

    try {
      setBusyAction((p) => ({ ...p, [usCode]: "deactivate" }));
      const requestBody = {
        adminId: Admin.pkid,
        userid: usCode,
        latitude,
        longitude,
      };

      await axios.post(
        `${baseUrl}/api/Admin/client/deactivateClient`,
        requestBody,
        config
      );
      await FetchClientCall();
      Swal.fire("Deactivated!", "The client has been deactivated.", "success");
    } catch (error) {
      console.error("deactivateClient error:", error);
      Swal.fire("Error!", "Something went wrong while deactivating.", "error");
    } finally {
      setBusyAction((p) => ({ ...p, [usCode]: null }));
    }
  };

  const ActivateClientAPI = async (usCode) => {
    const result = await Swal.fire({
      title: "Activate client?",
      text: "Client will be able to access the services.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, activate",
    });
    if (!result.isConfirmed) return;

    try {
      setBusyAction((p) => ({ ...p, [usCode]: "activate" }));
      const requestBody = {
        adminId: Admin.pkid,
        userid: usCode,
        latitude,
        longitude,
      };

      await axios.post(
        `${baseUrl}/api/Admin/client/activateClient`,
        requestBody,
        config
      );
      await FetchClientCall();
      Swal.fire("Activated!", "The client has been activated.", "success");
    } catch (error) {
      console.error("activateClient error:", error);
      Swal.fire("Error!", "Something went wrong while activating.", "error");
    } finally {
      setBusyAction((p) => ({ ...p, [usCode]: null }));
    }
  };

  const CredentialUpdateAPI = async () => {
    const result = await Swal.fire({
      title: "Update WhatsApp credentials?",
      text: `User: ${userID}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#0d6efd",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, update",
    });
    if (!result.isConfirmed) return;

    try {
      setBusyAction((p) => ({ ...p, [userID]: "whatsapp" }));
      const requestBody = {
        adminId: Admin.pkid,
        latitude,
        longitude,
        userid: userID,
        appId,
        businessAccId,
        token,
        phoneNoId: phoneNo,
      };

      await axios.post(
        `${baseUrl}/api/Admin/client/configureWhatsappCredentials`,
        requestBody,
        config
      );
      await FetchClientCall();
      Swal.fire("Updated!", "Credentials have been updated.", "success");
      setuserID("");
    } catch (error) {
      console.error("configureWhatsappCredentials error:", error);
      Swal.fire("Error!", "Something went wrong while updating.", "error");
    } finally {
      setBusyAction((p) => ({ ...p, [userID]: null }));
    }
  };

  const registerMobileNumber = async (usCode) => {
    const result = await Swal.fire({
      title: "Register WhatsApp number?",
      text: `Call facebookRegisterMobileNumber for user: ${usCode}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#20c997",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, register",
    });
    if (!result.isConfirmed) return;

    try {
      setBusyAction((prev) => ({ ...prev, [usCode]: "register" }));
      const res = await axios.post(
        `${baseUrl}/api/Account/v1/facebookRegisterMobileNumber`,
        { userid: usCode },
        config
      );
      if (res.data?.responseCode === "00") {
        Swal.fire("Success", "Registration request sent.", "success");
      } else {
        Swal.fire(
          "Failed",
          res.data?.responseMessage || "Registration failed.",
          "error"
        );
      }
    } catch (error) {
      console.error("facebookRegisterMobileNumber error:", error);
      Swal.fire("Error", "Unable to register the number.", "error");
    } finally {
      setBusyAction((prev) => ({ ...prev, [usCode]: null }));
    }
  };

  const subscribeMobileNumber = async (usCode) => {
    const result = await Swal.fire({
      title: "Subscribe WhatsApp webhook?",
      text: `Call facebookSubscribe for user: ${usCode}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6f42c1",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, subscribe",
    });
    if (!result.isConfirmed) return;

    try {
      setBusyAction((prev) => ({ ...prev, [usCode]: "subscribe" }));
      const res = await axios.post(
        `${baseUrl}/api/Account/v1/facebookSubscribe`,
        { userid: usCode },
        config
      );
      if (res.data?.responseCode === "00") {
        Swal.fire("Subscribed", "Webhook subscription created.", "success");
      } else {
        Swal.fire(
          "Failed",
          res.data?.responseMessage || "Subscription failed.",
          "error"
        );
      }
    } catch (error) {
      console.error("facebookSubscribe error:", error);
      Swal.fire("Error", "Unable to subscribe.", "error");
    } finally {
      setBusyAction((prev) => ({ ...prev, [usCode]: null }));
    }
  };

  const [ExtendModalOpen, setExtendModalOpen] = useState();
  const ExtendSubscriptionAPI = async () => {
    const result = await Swal.fire({
      title: "Confirm Upgrade?",
      html: `
      <div style="text-align:left">
        <div><b>User:</b> ${userID}</div>
        <div><b>Days:</b> ${extendDays}</div>
      </div>
    `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#0d6efd",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, proceed",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      allowOutsideClick: false,
    });

    if (!result.isConfirmed) return;

    try {
      setBusyAction((p) => ({ ...p, [userID]: "upgrade" }));
      setExtendLoading(true);

      const requestBody = {
        userid: userID,
        days: extendDays,
      };

      const res = await axios.post(
        `${baseUrl}/api/Admin/v1.0/extendUserSubscription`,
        requestBody,
        config
      );

      if (res?.data?.responseCode === "00") {
        setExtendLoading(false);

        return Swal.fire(
          "Upgraded!",
          res?.data?.responseMessage ||
          res?.data?.response?.message ||
          "Subscription extended successfully.",
          "success"
        ).then(() => {
          setExtendModalOpen(false);
        });
      } else {
        await Swal.fire(
          "Failed",
          res?.data?.responseMessage || "Unable to extend subscription.",
          "error"
        );
      }
    } catch (error) {
      console.error("extendUserSubscription error:", error);
      await Swal.fire(
        "Error",
        "Something went wrong while extending subscription.",
        "error"
      );
    } finally {
      setExtendLoading(false);
      setBusyAction((p) => ({ ...p, [userID]: null }));
    }
  };
  const UpdateClientPaymentGatewayAPI = async () => {
    if (!userID || !paymentGateway?.trim()) {
      return toast.error("Please enter a payment gateway value.");
    }

    const result = await Swal.fire({
      title: "Update Payment Gateway?",
      html: `<div style="text-align:left"><b>User:</b> ${userID}<br/><b>Gateway:</b> ${paymentGateway}</div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#0d6efd",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, update",
    });
    if (!result.isConfirmed) return;

    try {
      setBusyAction((p) => ({ ...p, [userID]: "gateway" }));

      const body = {
        adminId: Admin.pkid,
        userid: userID,
        paymentGateway: paymentGateway.trim(),
      };

      const res = await axios.post(
        `${baseUrl}/api/Admin/v1.0/updateClientPaymentGateway`,
        body,
        config
      );

      if (res?.data?.responseCode === "00") {
        Swal.fire("Updated!", res?.data?.responseMessage || "Payment gateway updated.", "success");
        setShowGatewayForm(false);
        // optional: refresh list
        // await FetchClientCall();
      } else {
        Swal.fire("Failed", res?.data?.responseMessage || "Unable to update gateway.", "error");
      }
    } catch (err) {
      console.error("updateClientPaymentGateway error:", err);
      Swal.fire("Error", "Something went wrong while updating gateway.", "error");
    } finally {
      setBusyAction((p) => ({ ...p, [userID]: null }));
    }
  };


  // Effects #######################################################################################################
  useEffect(() => {
    FetchClientCall();
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
        },
        () => { }
      );
    }
  }, []);

  useEffect(() => {
    setAppId(Credentials?.app_id || "");
    setBusinessAccId(Credentials?.business_acc_id || "");
    setPhoneNo(Credentials?.phone_no_id || "");
    setToken(Credentials?.token || "");
  }, [Credentials]);

  // Helpers #######################################################################################################
  const filteredClients = Clients?.filter((client) => {
    const orgName = client?.organization_name?.toLowerCase() || "";
    const status = client?.us_status || "";
    const lastLogin = client?.us_last_login || "";

    const matchSearch = orgName.includes(search.toLowerCase());
    const matchStatus = statusFilter ? status === statusFilter : true;
    const matchDate = dateFilter ? lastLogin.includes(dateFilter) : true;

    return matchSearch && matchStatus && matchDate;
  });

  const viewCatelogue = (clientId) =>
    navigate(`/admin/ClientCatalogue/${clientId}`);

  const StatusBadgeClient = ({ status }) => {
    const isActive = status === "1";
    const styles = {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: "999px",
      fontSize: "0.85rem",
      fontWeight: "600",
      backgroundColor: isActive ? "#e8f5e9" : "#ffebee",
      color: isActive ? "#2e7d32" : "#c62828",
      border: `1px solid ${isActive ? "#c8e6c9" : "#ffcdd2"}`,
      gap: "6px",
      whiteSpace: "nowrap",
    };
    return (
      <span style={styles}>
        {isActive ? <UserCheck size={16} /> : <UserX size={16} />}
        {isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  // UI ###########################################################################################################
  return (
    <div>
      <NoIndex />
      <div className="marginRight px-4 mt-4 bg-white">
        <div className="border p-3 rounded-3 mb-3 bg-light admin-popup">
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <h2 className="m-0">All Clients</h2>
            <div className="d-flex flex-wrap gap-2 mt-2 mt-md-0">
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ minWidth: 220 }}
                placeholder="Search by Organization"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select
                className="form-select form-select-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        {Loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="scrollable-table border-radius-10-left">
            <table className="table table-bordered table-hover align-middle">
              <thead
                className="text-center"
                style={{ backgroundColor: "#01C37C" }}
              >
                <tr>
                  <th className="th-style">Organization</th>
                  <th className="th-style">User</th>
                  <th className="th-style">Code</th>
                  <th className="th-style">Contact</th>
                  <th className="th-style">Login ID</th>
                  <th className="th-style">Last Login</th>
                  {/* <th className="th-style">ID</th> */}
                  <th className="th-style">Wallet Balance</th>
                  <th className="th-style">Status</th>
                  <th className="th-style">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients?.map((client) => (
                  <tr key={client.id}>
                    <td className="text-center">
                      {client.organization_name || "N/A"}
                    </td>
                    <td className="text-center">{client.us_name || "N/A"}</td>
                    <td className="text-center">
                      <strong>{client.us_code || "N/A"}</strong>
                    </td>
                    <td className="text-center">
                      {client.us_contact || client.us_loginid || "N/A"}
                    </td>
                    <td className="text-center">
                      {client.us_loginid || "N/A"}
                    </td>
                    <td className="text-center">
                      {client.us_last_login || "N/A"}
                    </td>
                    {/* <td className="text-center">{client.id || "N/A"}</td> */}
                    <td className="text-center">{client.balance || "N/A"}</td>
                    <td className="text-center">
                      <StatusBadgeClient status={client.us_status} />
                    </td>
                    <td className="text-center">
                      {/* Single compact Details button */}
                      <button
                        className="admin-btn success"
                        onClick={() => {
                          setSelectedClient(client);
                          setuserID(client.us_code);
                          setPaymentGateway("");        // NEW
                          setShowGatewayForm(false);    // NEW
                          setShowDetails(true);
                        }}
                        title="View Details"
                        style={{ width: 100 }}
                      >
                        <Eye size={16} style={{ marginRight: 6 }} />
                        Details
                      </button>

                    </td>
                  </tr>
                ))}
                {filteredClients?.length === 0 && (
                  <tr>
                    <td colSpan="10" className="text-center text-muted py-4">
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal (centralizes all actions) */}
      <div
        className={`modal fade ${showDetails ? "show d-block" : ""}`}
        tabIndex="-1"
        aria-hidden={!showDetails}
        style={{ background: showDetails ? "rgba(0,0,0,.4)" : "transparent" }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5">
                Client Details —{" "}
                <span className="text-primary">
                  {selectedClient?.organization_name ?? "—"}
                </span>
              </h1>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowDetails(false)}
              />
            </div>

            <div className="modal-body">
              {!selectedClient ? (
                <div className="text-center py-4 text-muted">No data</div>
              ) : (
                <>
                  {/* Summary grid */}
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="border rounded p-3 h-100">
                        <h6 className="text-muted mb-2">Basic</h6>
                        <div className="small"><b>Organization</b>: {selectedClient.organization_name || "N/A"}</div>
                        <div className="small"><b>User Name</b>: {selectedClient.us_name || "N/A"}</div>
                        <div className="small"><b>User Code</b>: {selectedClient.us_code || "N/A"}</div>
                        <div className="small"><b>Login ID</b>: {selectedClient.us_loginid || "N/A"}</div>
                        <div className="small"><b>Email</b>: {selectedClient.us_email || "N/A"}</div>
                        <div className="small"><b>ID</b>: {selectedClient.id || "N/A"}</div>
                        <div className="small"><b>Status</b>:{" "}
                          <StatusBadgeClient status={selectedClient.us_status} />
                        </div>
                        <div className="small"><b>Last Login</b>: {selectedClient.us_last_login || "N/A"}</div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="border rounded p-3 h-100">
                        <h6 className="text-muted mb-2">Accounts</h6>
                        <div className="small"><b>Wallet Balance</b>: {selectedClient.balance ?? "N/A"}</div>
                        <div className="small"><b>Total Campaigns</b>: {selectedClient.totalCampaigns ?? "0"}</div>
                        <div className="small"><b>Total Templates</b>: {selectedClient.totalTemplates ?? "0"}</div>
                        <div className="small d-flex align-items-center gap-2 mt-2">
                          <b>Deep Link</b>:
                          {selectedClient.deep_link ? (
                            <a
                              href={selectedClient.deep_link}
                              target="_blank"
                              rel="noreferrer"
                              className="small d-inline-flex align-items-center"
                            >
                              Open <ExternalLink size={14} style={{ marginLeft: 6 }} />
                            </a>
                          ) : (
                            "N/A"
                          )}
                        </div>
                        <div className="small mt-2">
                          <b>QR Code</b>:
                          <div className="mt-2">
                            {selectedClient.qr_code ? (
                              <img
                                src={selectedClient.qr_code}
                                alt="QR"
                                style={{ maxWidth: "180px", borderRadius: 6, border: "1px solid #eee" }}
                              />
                            ) : (
                              <span className="text-muted"> N/A</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions inside modal */}
                  <div className="border rounded p-3 mt-3">
                    <h6 className="text-muted mb-2">Actions</h6>
                    <div className="admin-btn-group">
                      <button
                        className="admin-btn purple"
                        onClick={() => subscribeMobileNumber(selectedClient.us_code)}
                        disabled={!!busyAction[selectedClient.us_code]}
                      >
                        {busyAction[selectedClient.us_code] === "subscribe"
                          ? "Subscribing…"
                          : "Subscribe"}
                      </button>

                      <button
                        className="admin-btn teal"
                        onClick={() => registerMobileNumber(selectedClient.us_code)}
                        disabled={!!busyAction[selectedClient.us_code]}
                      >
                        {busyAction[selectedClient.us_code] === "register"
                          ? "Registering…"
                          : "Register"}
                      </button>

                      <button
                        className="admin-btn primary"
                        onClick={() => {
                          setuserID(selectedClient.us_code);
                          FetchClientCredentialsCall(selectedClient.us_code);
                          setShowWhatsAppModal(true); // open controlled modal
                        }}
                        disabled={!!busyAction[selectedClient.us_code]}
                      >
                        WhatsApp
                      </button>
                      <button
                        className="admin-btn warning"
                        onClick={() => {
                          setuserID(selectedClient.us_code);
                          setExtendDays("30");
                          setShowExtendModal(true); // open controlled modal
                        }}
                        disabled={!!busyAction[selectedClient.us_code]}
                      >
                        Upgrade
                      </button>


                      <button
                        className="admin-btn info"
                        onClick={() => {
                          setShowConfigModal(true);
                        }}
                        disabled={!!busyAction[selectedClient.us_code]}
                      >
                        Configuration
                      </button>
                      <button
                        className="admin-btn secondary"
                        onClick={() => {
                          setuserID(selectedClient.us_code);
                          setShowGatewayForm((v) => !v);
                        }}
                        disabled={!!busyAction[selectedClient.us_code]}
                      >
                        Gateway
                      </button>


                      {selectedClient.us_status === "1" ? (
                        <button
                          className="admin-btn danger"
                          onClick={() => DeactivateClientAPI(selectedClient.us_code)}
                          disabled={!!busyAction[selectedClient.us_code]}
                        >
                          {busyAction[selectedClient.us_code] === "deactivate" ? "Deactivating…" : "Deactivate"}
                        </button>
                      ) : (
                        <button
                          className="admin-btn success"
                          onClick={() => ActivateClientAPI(selectedClient.us_code)}
                          disabled={!!busyAction[selectedClient.us_code]}
                        >
                          {busyAction[selectedClient.us_code] === "activate" ? "Activating…" : "Activate"}
                        </button>
                      )}
                    </div>
                    {showGatewayForm && (
                      <div className="mt-3 p-3 border rounded">
                        <div className="row g-2 align-items-end">
                          <div className="col-md-6">
                            <label className="form-label">User ID</label>
                            <input className="form-control" value={userID} readOnly />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Payment Gateway</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="e.g. razorpay / cashfree / payu"
                              value={paymentGateway}
                              onChange={(e) => setPaymentGateway(e.target.value)}
                            />
                          </div>
                          <div className="col-12 d-flex gap-2 mt-2">
                            <button
                              className="mini-btn primary"
                              onClick={UpdateClientPaymentGatewayAPI}
                              disabled={!!busyAction[userID]}
                            >
                              {busyAction[userID] === "gateway" ? "Saving…" : "Save"}
                            </button>
                            <button
                              className="mini-btn ghost"
                              onClick={() => setShowGatewayForm(false)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="text-muted small mt-2">
                      Tip: While any action is processing, all action buttons for this user stay disabled to prevent duplicate requests.
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="mini-btn ghost"
                onClick={() => setShowDetails(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal: Upgrade / Extend Subscription */}
      {/* Upgrade / Extend Subscription (controlled modal) */}
      <div
        className={`modal fade ${showExtendModal ? "show d-block" : ""}`}
        tabIndex="-1"
        aria-hidden={!showExtendModal}
        style={{ background: showExtendModal ? "rgba(0,0,0,.4)" : "transparent" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5">
                Upgrade Subscription — <span className="text-primary">{userID || "—"}</span>
              </h1>
              <button type="button" className="btn-close" onClick={() => setShowExtendModal(false)} />
            </div>

            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">User ID</label>
                  <input type="text" className="form-control" value={userID} readOnly />
                </div>
                <div className="col-12">
                  <label className="form-label">No. of Days</label>
                  <select
                    className="form-select"
                    value={extendDays}
                    onChange={(e) => setExtendDays(e.target.value)}
                  >
                    <option value="7">7 days</option>
                    <option value="15">15 days</option>
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="180">180 days</option>
                    <option value="365">365 days</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="mini-btn ghost" onClick={() => setShowExtendModal(false)}>
                Close
              </button>
              <button
                type="button"
                className="mini-btn primary"
                disabled={extendLoading || !userID || !extendDays || !!busyAction[userID]}
                onClick={async () => {
                  await ExtendSubscriptionAPI();
                  // if success, the API function already closes via setExtendModalOpen(false)
                  // ensure we close the controlled modal as well
                  setShowExtendModal(false);
                }}
              >
                {extendLoading ? "Processing..." : "Confirm Upgrade"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* WhatsApp Credentials (controlled modal) */}
      <div
        className={`modal fade ${showWhatsAppModal ? "show d-block" : ""}`}
        tabIndex="-1"
        aria-hidden={!showWhatsAppModal}
        style={{ background: showWhatsAppModal ? "rgba(0,0,0,.4)" : "transparent" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5">WhatsApp Credentials — <span className="text-primary">{userID || "—"}</span></h1>
              <button type="button" className="btn-close" onClick={() => setShowWhatsAppModal(false)} />
            </div>

            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">App ID</label>
                  <input className="form-control" value={appId} onChange={(e) => setAppId(e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label">Business Account ID</label>
                  <input className="form-control" value={businessAccId} onChange={(e) => setBusinessAccId(e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label">Phone No. ID</label>
                  <input className="form-control" value={phoneNo} onChange={(e) => setPhoneNo(e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label">Token</label>
                  <textarea className="form-control" rows={3} value={token} onChange={(e) => setToken(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="mini-btn ghost" onClick={() => setShowWhatsAppModal(false)}>Close</button>
              <button
                className="mini-btn primary"
                onClick={async () => {
                  await CredentialUpdateAPI();
                  setShowWhatsAppModal(false);
                }}
                disabled={!!busyAction[userID]}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>



      {/* Restaurent configure modal */}
      <ClientConfigModal
        show={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        client={selectedClient}
        baseUrl={baseUrl}
        config={config}
        Admin={Admin}
      />

      {/* Tiny in-file styles for the custom small buttons */}
      <style>{`
      .admin-btn {
        --bg: #f9fafb;
        --border: #d1d5db;
        --text: #111827;
        width: 90px;
        height: 28px;
        font-size: 0.75rem;
        font-weight: 600;
        border-radius: 4px;
        border: 1px solid var(--border);
        background: var(--bg);
        color: var(--text);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease-in-out;
        cursor: pointer;
        text-align: center;
        white-space: nowrap;
      }
      .admin-btn:hover {
        filter: brightness(1.08);
        transform: translateY(-1px);
      }
      .admin-btn:active {
        transform: translateY(0);
      }
      .admin-btn.primary { --bg:#01C37D; --border:#00a46a; --text:#ffffff; }
      .admin-btn.success { --bg:#4CAF50; --border:#3d8b40; --text:#ffffff; }
      .admin-btn.danger  { --bg:#E74C3C; --border:#c0392b; --text:#ffffff; }
      .admin-btn.teal    { --bg:#20B2AA; --border:#148f88; --text:#ffffff; }
      .admin-btn.purple  { --bg:#6C63FF; --border:#5146d9; --text:#ffffff; }
      .admin-btn.warning { --bg:#F4A300; --border:#c77f00; --text:#212529; }
      .admin-btn.info    { --bg:#0dcaf0; --border:#0bb3d5; --text:#0b2c3a; }
      .admin-btn.secondary { --bg:#e9ecef; --border:#d1d5db; --text:#111827; }


      .admin-btn:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(37, 99, 235, .3);
      }
      .admin-btn-group {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
      }
      `}</style>

      <ToastContainer />
    </div>
  );
};

export default ClientAccordionTable;
